/**
 * MattersFab — globalny FAB „📋 Zgłoś sprawę".
 *
 * 2 tryby:
 *   - Zalogowany: lokalizacja i autor pobrane z `user`/`location` props
 *   - Niezalogowany (login screen): hub z opcjami + gest odblokowujący (jeśli ustawiony w Settings)
 *     + dodatkowo: widok „Historia" pokazujący zgłoszenia z localStorage
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Modal from './ui/Modal';
import PatternLock from './PatternLock';
import MattersHistory from './MattersHistory';
import { compressImageFile } from '../utils/compressImage';

const API = process.env.REACT_APP_BACKEND_URL;
const HISTORY_KEY = 'mr_jerry_my_submissions';

const PRIORITY_OPTIONS = [
  { id: 'low', label: '🟢 Niski', color: '#22c55e' },
  { id: 'medium', label: '🟡 Średni', color: '#eab308' },
  { id: 'urgent', label: '🟠 Pilny', color: '#f97316' },
  { id: 'critical', label: '🔴 KRYTYCZNY (na już)', color: '#ef4444' },
];

const LOCATION_OPTIONS = [
  { id: 'krakow', label: '🏰 Kraków' },
  { id: 'nowysacz', label: '🏔️ Nowy Sącz' },
  { id: 'grodek', label: '🏞️ Gródek nad Dunajcem' },
];

const FAB_SIZE = 56;

// Zapisz zgłoszenie do localStorage (do widoku Historia)
const saveLocalSubmission = (entry) => {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    const updated = [entry, ...arr].slice(0, 50);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
  } catch {
    /* localStorage może nie być dostępny — ignoruj */
  }
};

// Porównanie wzorów (kolejność punktów ma znaczenie)
const patternsMatch = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
};

const MattersFab = ({ user, location, anonymous = false, fixedRight = 24 + 60 + 12, positionTop = null, hideDefaultButton = false, externalOpen = null, onExternalClose = null, crossLocationTarget = null, crossLocationLabel = null }) => {
  const [hubOpen, setHubOpen] = useState(false);  // hub z 3 opcjami (anonymous mode)
  const [view, setView] = useState('location'); // 'location' | 'hub' | 'form' | 'history'
  const [needsUnlock, setNeedsUnlock] = useState(false);
  const [savedPattern, setSavedPattern] = useState(null);
  const [patternForLoc, setPatternForLoc] = useState(null); // zapamiętana lokalizacja, dla której zweryfikowano wzór

  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState('matter'); // 'matter' | 'inquiry'
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('medium');
  const [description, setDescription] = useState('');
  // pola tylko dla trybu anonymous (login screen)
  const [chosenLocation, setChosenLocation] = useState(location || '');
  const [authorName, setAuthorName] = useState('');
  const [saving, setSaving] = useState(false);
  const [photos, setPhotos] = useState([]); // [{ id, dataUrl }]
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const MAX_PHOTOS = 5;

  // W trybie anonymous: pobierz pattern z backendu na podstawie WYBRANEJ przez usera lokalizacji
  // (nie z props.location — bo user może chcieć zgłosić sprawę dla innego lokalu niż ten z LoginScreen)
  const fetchPatternForLocation = useCallback(async (loc) => {
    if (!loc) return null;
    try {
      const r = await axios.get(`${API}/api/settings/matters-pattern?location=${loc}`);
      return r.data.pattern || null;
    } catch {
      return null;
    }
  }, []);

  // Klik w FAB — w anonymous mode zaczynamy od wyboru lokalizacji
  const handleFabClick = useCallback(() => {
    if (anonymous) {
      setHubOpen(true);
      // Cross-location — lokalizacja już ustawiona z prop; pomijamy wybór, idziemy do hub-a od razu
      if (crossLocationTarget) {
        setView('hub');
      } else if (chosenLocation && patternForLoc === chosenLocation) {
        // Jeśli już wybrana lokalizacja i wzór był odblokowany dla niej w tej sesji → idziemy do hubu
        setView('hub');
      } else {
        setView('location');
      }
    } else {
      setOpen(true);
    }
  }, [anonymous, chosenLocation, patternForLoc, crossLocationTarget]);

  // Support external open trigger (used by LoginScreenLuxe custom buttons)
  useEffect(() => {
    if (externalOpen) {
      handleFabClick();
    }
  }, [externalOpen, handleFabClick]);

  const pickLocation = async (loc) => {
    setChosenLocation(loc);
    const p = await fetchPatternForLocation(loc);
    setSavedPattern(p);
    if (p && p.length >= 4) {
      setNeedsUnlock(true);
    } else {
      setPatternForLoc(loc); // nic do odblokowania
      setView('hub');
    }
  };

  const handleUnlockComplete = (drawn) => {
    if (patternsMatch(drawn, savedPattern)) {
      setNeedsUnlock(false);
      setPatternForLoc(chosenLocation); // zapamiętaj że odblokowano dla tej lokalizacji
      setView('hub');
      toast.success('🔓 Odblokowano');
    } else {
      toast.error('❌ Nieprawidłowy wzór');
    }
  };

  const openForm = (k) => {
    setKind(k);
    setView('form');
  };
  // Helper: czy modal jest otwarty? W anonymous mode → hubOpen, inaczej → open.
  const modalOpen = anonymous ? hubOpen : open;
  const closeModal = () => {
    if (saving) return;
    reset();
    if (anonymous) {
      setHubOpen(false);
      setView('location');
      setNeedsUnlock(false);
    } else {
      setOpen(false);
    }
  };
  // Tytuł modalu zależnie od widoku
  const modalTitle = (() => {
    if (anonymous) {
      if (view === 'location') return '📍 Wybierz lokalizację';
      if (view === 'hub') return '📋 Sprawy / Zapytania';
      if (view === 'history') return '🕘 Moja Historia';
    }
    return kind === 'inquiry' ? '❓ Zadaj zapytanie' : '📋 Zgłoś sprawę do managera';
  })();


  const reset = () => {
    setKind('matter');
    setTitle(''); setPriority('medium'); setDescription('');
    setChosenLocation(location || '');
    setAuthorName('');
    setPhotos([]);
  };

  const handlePickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      toast.error(`Maksymalnie ${MAX_PHOTOS} zdjęć`);
      return;
    }
    const slice = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.message(`Dodaję pierwsze ${remaining} z ${files.length} (limit ${MAX_PHOTOS})`);
    }
    setUploading(true);
    try {
      const compressed = await Promise.all(
        slice.map((f) => compressImageFile(f, { maxWidth: 1280, quality: 0.72, targetKB: 400 }))
      );
      setPhotos((prev) => [...prev, ...compressed.map((d) => ({ id: Math.random().toString(36).slice(2), dataUrl: d }))]);
      toast.success(`📸 Dodano ${slice.length} ${slice.length === 1 ? 'zdjęcie' : 'zdjęć'}`);
    } catch (err) {
      toast.error('Błąd przetwarzania zdjęcia: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (id) => {
    setPhotos((prev) => prev.filter((p) => p.id !== id));
  };

  const finalLocation = anonymous ? chosenLocation : location;
  const finalCreatedBy = anonymous ? 'anonim' : (user?.username || '');
  const finalCreatedByName = anonymous
    ? authorName.trim()
    : (`${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.username || '');

  const submit = async () => {
    if (anonymous && !finalLocation) { toast.error('Wybierz lokalizację'); return; }
    if (anonymous && !authorName.trim()) { toast.error('Wpisz swoje imię'); return; }

    // ZAPYTANIE — krótszy form
    if (kind === 'inquiry') {
      if (description.trim().length < 3) { toast.error('Wpisz pytanie (min. 3 znaki)'); return; }
      setSaving(true);
      try {
        const r = await axios.post(`${API}/api/restaurant-inquiries`, {
          location: finalLocation,
          question: description.trim(),
          askedByName: finalCreatedByName || finalCreatedBy || 'anonim',
        });
        // Zapisz lokalnie dla widoku Historia (anonymous mode)
        if (anonymous && r.data?.id) {
          saveLocalSubmission({
            type: 'inquiry',
            id: r.data.id,
            location: finalLocation,
            title: description.trim().slice(0, 80),
            createdAt: r.data.createdAt || new Date().toISOString(),
          });
        }
        try {
          window.dispatchEvent(new CustomEvent('inquiry:created', { detail: r.data }));
        } catch { /* ignore */ }
        toast.success('✅ Zapytanie wysłane — odpowiedź pojawi się w dzwoneczku 🔔');
        reset();
        if (anonymous) { setView('hub'); } else { setOpen(false); }
      } catch (e) {
        toast.error('Błąd wysyłania: ' + (e?.response?.data?.detail || e.message));
      } finally { setSaving(false); }
      return;
    }

    // ZGŁOSZENIE SPRAWY — pełny form (jak wcześniej)
    if (!title.trim()) { toast.error('Wpisz krótki tytuł sprawy'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/restaurant-matters/matters`, {
        // Cross-location: sprawa idzie DO właściciela (nowysacz), ale z sourceLocation=krakow.
        // Standardowo: location = wybrana lokalizacja, sourceLocation = null.
        location: crossLocationTarget || finalLocation,
        sourceLocation: crossLocationTarget ? finalLocation : null,
        title: title.trim(),
        description: description.trim(),
        priority,
        createdBy: finalCreatedBy,
        createdByName: finalCreatedByName,
        photos: photos.map((p) => p.dataUrl),
      });
      if (anonymous && r.data?.id) {
        saveLocalSubmission({
          type: 'matter',
          id: r.data.id,
          location: finalLocation,
          title: title.trim(),
          createdAt: r.data.createdAt || new Date().toISOString(),
        });
      }
      // Powiadom otwarte sekcje (MattersSection) o nowej sprawie — Optimistic UI bez fullreload
      try {
        window.dispatchEvent(new CustomEvent('matter:created', { detail: r.data }));
      } catch { /* ignore */ }
      toast.success('✅ Zgłoszenie wysłane do managera');
      reset();
      if (anonymous) { setView('hub'); } else { setOpen(false); }
    } catch (e) {
      toast.error('Błąd wysyłania: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        onClick={handleFabClick}
        aria-label="Zgłoś sprawę do managera"
        title="Zgłoś sprawę do managera"
        data-testid="matters-fab"
        className="matters-fab-btn"
        style={{
          position: 'fixed',
          ...(positionTop != null ? { top: positionTop } : { bottom: 24 }),
          right: fixedRight,
          display: hideDefaultButton ? 'none' : 'flex',
          width: FAB_SIZE,
          height: FAB_SIZE,
          borderRadius: '50%',
          background: 'linear-gradient(140deg, #1e293b 0%, #0f172a 100%)',
          color: '#f8fafc',
          border: '1.5px solid rgba(212, 175, 55, 0.55)',
          cursor: 'pointer',
          boxShadow: '0 10px 30px rgba(0,0,0,0.45), 0 0 0 4px rgba(212, 175, 55, 0.10), inset 0 1px 0 rgba(255,255,255,0.08)',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9997,
          transition: 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.25s',
          padding: 0,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.08) rotate(-6deg)';
          e.currentTarget.style.boxShadow = '0 14px 36px rgba(212,175,55,0.35), 0 0 0 6px rgba(212, 175, 55, 0.18), inset 0 1px 0 rgba(255,255,255,0.12)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
          e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.45), 0 0 0 4px rgba(212, 175, 55, 0.10), inset 0 1px 0 rgba(255,255,255,0.08)';
        }}
      >
        {/* Pulsujący pierścień */}
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: -4,
            borderRadius: '50%',
            border: '2px solid rgba(212, 175, 55, 0.45)',
            animation: 'mattersFabPulse 2.4s ease-out infinite',
            pointerEvents: 'none',
          }}
        />
        {/* Ikona — schowek/clipboard z plusem (zgłoś nową sprawę) */}
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ color: '#d4af37', filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.4))' }}>
          <path d="M9 4h6a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" />
          <path d="M8 5H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
          <path d="M12 11v6" />
          <path d="M9 14h6" />
        </svg>
        <style>{`
          @keyframes mattersFabPulse {
            0%   { transform: scale(1); opacity: 0.75; }
            70%  { transform: scale(1.35); opacity: 0; }
            100% { transform: scale(1.35); opacity: 0; }
          }
        `}</style>
      </button>

      {anonymous && needsUnlock && (
        <PatternLock
          mode="verify"
          currentPattern={savedPattern}
          onComplete={handleUnlockComplete}
          onCancel={() => setNeedsUnlock(false)}
        />
      )}

      {/* JEDEN modal dla wszystkich widoków — pozostaje zamontowany podczas zmiany
          widoków (hub ↔ form ↔ history). Eliminuje „przebłysk" backdropu. */}
      {modalOpen && (
        <Modal
          open
          onClose={closeModal}
          title={modalTitle}
          maxWidth={anonymous && view === 'history' ? 620 : (anonymous && (view === 'hub' || view === 'location') ? 460 : 520)}
          testId="matters-modal"
          align="top"
        >
          {/* === Anonymous LOCATION CHOOSER (krok 1) === */}
          {anonymous && view === 'location' && (
            <>
              <div style={{
                padding: 14, marginBottom: 16, borderRadius: 12,
                background: 'linear-gradient(135deg, rgba(212,175,55,0.14), rgba(212,175,55,0.04))',
                border: '1px solid rgba(212,175,55,0.30)',
                display: 'flex', alignItems: 'flex-start', gap: 10,
              }}>
                <span style={{ fontSize: 20, lineHeight: 1 }}>📍</span>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
                  Wybierz lokalizację, której dotyczy zgłoszenie. Jeśli dla niej ustawiony jest <strong style={{ color: '#d4af37' }}>wzór odblokowujący</strong>, pojawi się ekran weryfikacji.
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { id: 'krakow',   name: 'Kraków',              emoji: '🏰', color: '#a855f7', accent: 'rgba(168, 85, 247, 0.16)' },
                  { id: 'nowysacz', name: 'Nowy Sącz',           emoji: '🏔️', color: '#22c55e', accent: 'rgba(34, 197, 94, 0.16)'  },
                  { id: 'grodek',   name: 'Gródek nad Dunajcem', emoji: '🏞️', color: '#0ea5e9', accent: 'rgba(14, 165, 233, 0.16)' },
                ].map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => pickLocation(l.id)}
                    data-testid={`location-pick-${l.id}`}
                    className="location-pick-btn"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 14,
                      padding: '16px 18px', borderRadius: 14,
                      background: `linear-gradient(135deg, ${l.accent}, rgba(255,255,255,0.02))`,
                      border: `1.5px solid ${l.color}55`,
                      cursor: 'pointer', fontFamily: 'inherit',
                      textAlign: 'left', width: '100%',
                      boxShadow: `0 4px 16px ${l.color}10`,
                    }}
                  >
                    <div style={{
                      width: 48, height: 48, borderRadius: 12,
                      background: `linear-gradient(135deg, ${l.color}33, ${l.color}11)`,
                      border: `1px solid ${l.color}66`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 24, flexShrink: 0,
                    }}>
                      {l.emoji}
                    </div>
                    <div style={{
                      flex: 1, minWidth: 0,
                      fontWeight: 800, fontSize: 16, color: 'var(--text)',
                      letterSpacing: '-0.01em',
                    }}>
                      {l.name}
                    </div>
                    <div style={{
                      width: 32, height: 32, borderRadius: 16,
                      background: `${l.color}22`, border: `1px solid ${l.color}55`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: l.color, fontSize: 16, fontWeight: 700, flexShrink: 0,
                    }}>›</div>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* === Anonymous HUB === */}
          {anonymous && view === 'hub' && (
            <>
              <div style={{
                fontSize: 11, color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.5,
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '5px 10px', borderRadius: 999, background: 'rgba(212, 175, 55, 0.12)',
                border: '1px solid rgba(212, 175, 55, 0.30)',
              }}>
                📍 <strong style={{ color: '#d4af37' }}>{LOCATION_OPTIONS.find((l) => l.id === chosenLocation)?.label || chosenLocation}</strong>
                <button
                  type="button"
                  onClick={() => { setView('location'); setPatternForLoc(null); }}
                  data-testid="hub-change-location"
                  style={{
                    marginLeft: 6, padding: '2px 8px', borderRadius: 999, fontSize: 10,
                    background: 'transparent', border: '1px solid rgba(212, 175, 55, 0.40)',
                    color: 'var(--text-muted)', cursor: 'pointer', fontWeight: 700,
                  }}
                >
                  zmień
                </button>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
                Wybierz, co chcesz zrobić. Jeśli masz wcześniejsze zgłoszenia z tego urządzenia — zobaczysz je w „Historia&rdquo;.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button type="button" onClick={() => openForm('matter')} data-testid="hub-open-matter" style={hubBtnStyle('#f97316', 'rgba(249,115,22,0.16)')}>
                  <span style={{ fontSize: 22 }}>📋</span>
                  <span style={{ flex: 1, textAlign: 'left' }}>
                    <span style={{ display: 'block', fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>Zgłoś sprawę</span>
                    <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      Coś się zepsuło / brakuje / trzeba zamówić — manager zobaczy zgłoszenie
                    </span>
                  </span>
                  <span style={{ fontSize: 16, color: '#f97316' }}>›</span>
                </button>
                <button type="button" onClick={() => openForm('inquiry')} data-testid="hub-open-inquiry" style={hubBtnStyle('#3b82f6', 'rgba(59,130,246,0.16)')}>
                  <span style={{ fontSize: 22 }}>❓</span>
                  <span style={{ flex: 1, textAlign: 'left' }}>
                    <span style={{ display: 'block', fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>Zadaj pytanie</span>
                    <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      Manager odpowie — odpowiedź pojawi się w dzwoneczku 🔔
                    </span>
                  </span>
                  <span style={{ fontSize: 16, color: '#3b82f6' }}>›</span>
                </button>
                <button type="button" onClick={() => setView('history')} data-testid="hub-open-history" style={hubBtnStyle('#22c55e', 'rgba(34,197,94,0.16)')}>
                  <span style={{ fontSize: 22 }}>🕘</span>
                  <span style={{ flex: 1, textAlign: 'left' }}>
                    <span style={{ display: 'block', fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>Historia</span>
                    <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      Twoje zgłoszenia i pytania — status, odpowiedzi, oczekujące
                    </span>
                  </span>
                  <span style={{ fontSize: 16, color: '#22c55e' }}>›</span>
                </button>
              </div>
            </>
          )}

          {/* === Anonymous HISTORY === */}
          {anonymous && view === 'history' && (
            <>
              <button
                type="button"
                onClick={() => setView('hub')}
                data-testid="history-back"
                style={{
                  padding: '6px 12px', marginBottom: 14, borderRadius: 8, cursor: 'pointer',
                  background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-muted)',
                  fontSize: 12, fontWeight: 600,
                }}
              >
                ← Wstecz do menu
              </button>
              <MattersHistory location={chosenLocation} />
            </>
          )}

          {/* === FORM (Zgłoś sprawę / Zadaj zapytanie) === */}
          {(view === 'form' || !anonymous) && (
            <>
              {anonymous && (
                <button
                  type="button"
                  onClick={() => { reset(); setView('hub'); }}
                  data-testid="form-back-to-hub"
                  style={{
                    padding: '6px 12px', marginBottom: 14, borderRadius: 8, cursor: 'pointer',
                    background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-muted)',
                    fontSize: 12, fontWeight: 600,
                  }}
                >
                  ← Wstecz do menu
                </button>
              )}
              {/* Wybór typu: Zgłoś sprawę / Zapytanie — ukryty w anonymous (wybrane z huba) */}
              {!anonymous && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }} data-testid="matters-kind-switch">
            <button type="button" onClick={() => setKind('matter')} data-testid="matters-kind-matter"
              style={{
                flex: 1, padding: '10px 12px', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit',
                background: kind === 'matter' ? 'rgba(249,115,22,0.18)' : 'var(--bg)',
                border: `2px solid ${kind === 'matter' ? '#f97316' : 'var(--border)'}`,
                color: kind === 'matter' ? '#f97316' : 'var(--text)',
                fontSize: 13, fontWeight: 700,
              }}>📋 Zgłoś sprawę</button>
            <button type="button" onClick={() => setKind('inquiry')} data-testid="matters-kind-inquiry"
              style={{
                flex: 1, padding: '10px 12px', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit',
                background: kind === 'inquiry' ? 'rgba(59,130,246,0.18)' : 'var(--bg)',
                border: `2px solid ${kind === 'inquiry' ? '#3b82f6' : 'var(--border)'}`,
                color: kind === 'inquiry' ? '#3b82f6' : 'var(--text)',
                fontSize: 13, fontWeight: 700,
              }}>❓ Zadaj zapytanie</button>
          </div>
          )}

          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
            {kind === 'inquiry'
              ? 'Masz pytanie do managera? Napisz, manager odpowie — odpowiedź pojawi się w dzwoneczku 🔔 i wszyscy w lokalu będą mogli z niej skorzystać.'
              : 'Coś się zepsuło? Brakuje czegoś z magazynu? Trzeba coś zamówić lub naprawić w systemie? Wpisz krótko i wyślij — manager zobaczy zgłoszenie w module „Sprawy restauracji".'}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {anonymous && (
              <>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>
                    Lokalizacja
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                    {LOCATION_OPTIONS.map((l) => (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => setChosenLocation(l.id)}
                        data-testid={`matters-location-${l.id}`}
                        style={{
                          padding: '10px 8px', borderRadius: 10, cursor: 'pointer',
                          background: chosenLocation === l.id ? 'rgba(249, 115, 22, 0.18)' : 'var(--bg)',
                          border: `2px solid ${chosenLocation === l.id ? '#f97316' : 'var(--border)'}`,
                          color: chosenLocation === l.id ? '#f97316' : 'var(--text)',
                          fontSize: 12, fontWeight: 700,
                        }}
                      >
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>
                    Twoje imię i nazwisko
                  </label>
                  <input
                    type="text"
                    value={authorName}
                    maxLength={200}
                    onChange={(e) => setAuthorName(e.target.value)}
                    placeholder="np. Anna Kowalska"
                    style={inputStyle}
                    data-testid="matters-author-name"
                  />
                </div>
              </>
            )}

            {kind === 'matter' && (
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Krótki tytuł</label>
              <input
                type="text" value={title} maxLength={200}
                autoFocus={!anonymous}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="np. Skończyły się serwetki w szatni"
                style={inputStyle}
                data-testid="matters-title"
              />
            </div>
            )}
            {kind === 'matter' && (
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Priorytet</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {PRIORITY_OPTIONS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPriority(p.id)}
                    data-testid={`matters-priority-${p.id}`}
                    style={{
                      padding: '10px', borderRadius: 10, cursor: 'pointer',
                      background: priority === p.id ? `${p.color}22` : 'var(--bg)',
                      border: `2px solid ${priority === p.id ? p.color : 'var(--border)'}`,
                      color: priority === p.id ? p.color : 'var(--text)',
                      fontSize: 13, fontWeight: 700, transition: 'all 0.15s',
                    }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            )}
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>
                {kind === 'inquiry' ? 'Twoje pytanie' : 'Opis (opcjonalny)'}
              </label>
              <textarea
                value={description}
                maxLength={2000}
                rows={5}
                autoFocus={kind === 'inquiry'}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={kind === 'inquiry'
                  ? 'np. Jak rozwiązać sytuację z źle zamkniętym rachunkiem?'
                  : 'Dodatkowe szczegóły, lokalizacja, wymiary...'}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                data-testid="matters-description"
              />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', marginTop: 2 }}>
                {description.length} / 2000
              </div>
            </div>

            {kind === 'matter' && (
            /* Zdjęcia (opcjonalne) */
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>
                📸 Zdjęcia (opcjonalne, max {MAX_PHOTOS})
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePickFiles}
                style={{ display: 'none' }}
                data-testid="matters-photos-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || photos.length >= MAX_PHOTOS}
                data-testid="matters-photos-pick"
                style={{
                  width: '100%', padding: '12px',
                  borderRadius: 10, border: '2px dashed var(--border)',
                  background: 'var(--bg)', color: 'var(--text-muted)',
                  cursor: uploading || photos.length >= MAX_PHOTOS ? 'not-allowed' : 'pointer',
                  fontSize: 13, fontWeight: 600,
                  opacity: photos.length >= MAX_PHOTOS ? 0.5 : 1,
                }}
              >
                {uploading
                  ? '⏳ Przetwarzam zdjęcia…'
                  : photos.length >= MAX_PHOTOS
                    ? `Limit ${MAX_PHOTOS} zdjęć osiągnięty`
                    : '📷 Dodaj zdjęcia'}
              </button>
              {photos.length > 0 && (
                <div style={{
                  marginTop: 10,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                  gap: 6,
                }} data-testid="matters-photos-preview">
                  {photos.map((p) => (
                    <div key={p.id} style={{
                      position: 'relative',
                      paddingBottom: '100%',
                      borderRadius: 8,
                      overflow: 'hidden',
                      border: '1px solid var(--border)',
                      background: 'var(--bg)',
                    }}>
                      <img
                        src={p.dataUrl}
                        alt="załącznik"
                        style={{
                          position: 'absolute', inset: 0, width: '100%', height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(p.id)}
                        data-testid={`matters-photo-remove-${p.id}`}
                        title="Usuń zdjęcie"
                        style={{
                          position: 'absolute', top: 4, right: 4,
                          width: 22, height: 22, borderRadius: 11,
                          background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none',
                          cursor: 'pointer', fontSize: 12, lineHeight: 1,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
            <button
              onClick={() => {
                if (anonymous) {
                  reset();
                  setView('hub');
                } else {
                  reset();
                  setOpen(false);
                }
              }}
              disabled={saving}
              style={{
                flex: 1, padding: '10px', borderRadius: 10,
                background: 'var(--bg-card)', border: '1px solid var(--border)',
                color: 'var(--text)', fontWeight: 600, fontSize: 13,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {anonymous ? '← Wstecz' : 'Anuluj'}
            </button>
            <button
              onClick={submit}
              disabled={saving || (kind === 'matter' ? !title.trim() : description.trim().length < 3) || (anonymous && (!chosenLocation || !authorName.trim()))}
              data-testid="matters-submit"
              style={{
                flex: 2, padding: '10px', borderRadius: 10,
                background: (kind === 'matter' ? !title.trim() : description.trim().length < 3) || (anonymous && (!chosenLocation || !authorName.trim()))
                  ? 'var(--bg-card)'
                  : 'linear-gradient(135deg, #f97316, #ea580c)',
                color: (kind === 'matter' ? !title.trim() : description.trim().length < 3) || (anonymous && (!chosenLocation || !authorName.trim())) ? 'var(--text-muted)' : '#fff',
                border: 'none', fontWeight: 700, fontSize: 13,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Wysyłam...' : (kind === 'inquiry' ? '📤 Wyślij zapytanie' : '📤 Wyślij zgłoszenie')}
            </button>
          </div>
            </>
          )}
        </Modal>
      )}
    </>
  );
};

const inputStyle = {
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--input-bg)',
  color: 'var(--text)',
  fontSize: 13,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const hubBtnStyle = (border, bgActive) => ({
  display: 'flex', alignItems: 'center', gap: 14,
  padding: '14px 16px', borderRadius: 12,
  background: bgActive,
  border: `1px solid ${border}55`,
  cursor: 'pointer', fontFamily: 'inherit',
  textAlign: 'left', width: '100%',
  transition: 'border-color 160ms, background 160ms',
});

export default MattersFab;
